import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Upload, Download, Trash2, Search, FileSpreadsheet, Edit, Plus, MessageSquare,
  Users, CheckCircle2, Clock, AlertTriangle, Calendar, RefreshCw, FileDown
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = (s) => {
  const v = (s || '').toLowerCase();
  if (v === 'completed' || v === 'done') return 'bg-green-100 text-green-700';
  if (v === 'in progress') return 'bg-amber-100 text-amber-700';
  if (v === 'awaiting feedback') return 'bg-purple-100 text-purple-700';
  if (v === 'not started' || v === 'todo' || v === 'to do') return 'bg-slate-100 text-slate-700';
  return 'bg-slate-50 text-slate-600';
};
const PRIORITY_BADGE = (p) => {
  const v = (p || '').toLowerCase();
  if (v.includes('high')) return 'bg-red-100 text-red-700';
  if (v.includes('med')) return 'bg-blue-100 text-blue-700';
  if (v.includes('low')) return 'bg-slate-100 text-slate-600';
  return '';
};

export const WFHTrackerPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('board');
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [replaceAll, setReplaceAll] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  // Employee dialog
  const emptyEmp = { id: null, name: '', department: '', position: '', manager: '' };
  const [showEmpDialog, setShowEmpDialog] = useState(false);
  const [empForm, setEmpForm] = useState(emptyEmp);

  // Task dialog (add/edit)
  const emptyTask = {
    id: null, employee_name: '', department: '', position: '', manager: '',
    date: '', start_time: '', end_time: '', task: '', priority: 'Medium',
    milestone: '', due_date: '', status: 'Not Started', progress: '',
    blockers: '', support: '', comments: '', next_action: '', follow_up_date: '', approved_by: ''
  };
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [taskForm, setTaskForm] = useState(emptyTask);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tasksRes, empRes] = await Promise.all([
        axios.get(`${API}/wfh-tracker/tasks`),
        axios.get(`${API}/wfh-tracker/employees`),
      ]);
      setTasks(tasksRes.data || []);
      setEmployees(empRes.data || []);
    } catch (e) { toast.error('Failed to load WFH tracker'); }
    finally { setLoading(false); }
  };

  const handleImport = async () => {
    if (!importFile) { toast.error('Select an .xlsx file'); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await axios.post(`${API}/wfh-tracker/import?replace_all=${replaceAll}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported ${res.data.imported} rows (${res.data.skipped} skipped)`);
      setShowImport(false);
      setImportFile(null);
      setReplaceAll(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };

  const downloadBlob = async (url, filename) => {
    try {
      const res = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data]);
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link); link.click(); link.remove();
      toast.success('Download started');
    } catch (e) { toast.error(e.response?.data?.detail || 'Download failed'); }
  };

  const exportEmployee = (employee) => {
    const safe = employee.replace(/ /g, '_');
    let url = `${API}/wfh-tracker/export?employee=${encodeURIComponent(employee)}`;
    if (dateFrom) url += `&date_from=${dateFrom}`;
    if (dateTo) url += `&date_to=${dateTo}`;
    downloadBlob(url, `WFH_Report_${safe}.xlsx`);
  };

  const exportAll = () => {
    let url = `${API}/wfh-tracker/export-all`;
    const params = [];
    if (dateFrom) params.push(`date_from=${dateFrom}`);
    if (dateTo) params.push(`date_to=${dateTo}`);
    if (params.length) url += '?' + params.join('&');
    downloadBlob(url, 'WFH_Report_All_Employees.xlsx');
  };

  const handleDeleteTask = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    try { await axios.delete(`${API}/wfh-tracker/tasks/${id}`); toast.success('Deleted'); loadData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // ----- Employee CRUD -----
  const openAddEmployee = () => { setEmpForm(emptyEmp); setShowEmpDialog(true); };
  const openEditEmployee = (emp) => {
    setEmpForm({ id: emp.id || null, name: emp.employee_name, department: emp.department || '', position: emp.position || '', manager: emp.manager || '' });
    setShowEmpDialog(true);
  };
  const handleSaveEmployee = async () => {
    if (!empForm.name.trim()) { toast.error('Name is required'); return; }
    const body = { name: empForm.name.trim(), department: empForm.department || null, position: empForm.position || null, manager: empForm.manager || null };
    try {
      if (empForm.id) {
        await axios.put(`${API}/wfh-tracker/employees/${empForm.id}`, body);
        toast.success('Employee updated');
      } else {
        await axios.post(`${API}/wfh-tracker/employees`, body);
        toast.success('Employee added');
      }
      setShowEmpDialog(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleDeleteEmployee = async (emp) => {
    if (!emp.id) { toast.error('Only manually-added employees can be deleted from here. Delete their tasks instead.'); return; }
    const wipeTasks = emp.total_tasks > 0 ? window.confirm(`${emp.employee_name} has ${emp.total_tasks} tasks. Delete those tasks too? OK = delete employee + tasks. Cancel = keep tasks.`) : false;
    if (!window.confirm(`Permanently remove employee "${emp.employee_name}"?`)) return;
    try {
      await axios.delete(`${API}/wfh-tracker/employees/${emp.id}?delete_tasks=${wipeTasks}`);
      toast.success('Employee removed');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // ----- Task CRUD -----
  const openAddTask = (employeeName) => {
    const emp = employees.find(e => e.employee_name === employeeName);
    setTaskForm({
      ...emptyTask,
      employee_name: employeeName || '',
      department: emp?.department || '',
      position: emp?.position || '',
      manager: emp?.manager || '',
      date: new Date().toISOString().slice(0, 10),
    });
    setShowTaskDialog(true);
  };
  const openEditTask = (t) => {
    setTaskForm({
      id: t.id,
      employee_name: t.employee_name || '',
      department: t.department || '', position: t.position || '', manager: t.manager || '',
      date: t.date || '', start_time: t.start_time || '', end_time: t.end_time || '',
      task: t.task || '', priority: t.priority || 'Medium', milestone: t.milestone || '',
      due_date: t.due_date || '', status: t.status || 'Not Started',
      progress: t.progress != null ? String(t.progress) : '',
      blockers: t.blockers || '', support: t.support || '', comments: t.comments || '',
      next_action: t.next_action || '', follow_up_date: t.follow_up_date || '', approved_by: t.approved_by || ''
    });
    setShowTaskDialog(true);
  };
  const handleSaveTask = async () => {
    if (!taskForm.employee_name.trim()) { toast.error('Employee is required'); return; }
    if (!taskForm.task.trim()) { toast.error('Task description is required'); return; }
    const payload = { ...taskForm };
    delete payload.id;
    payload.progress = payload.progress === '' ? null : parseFloat(payload.progress);
    Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
    try {
      if (taskForm.id) {
        await axios.put(`${API}/wfh-tracker/tasks/${taskForm.id}`, payload);
        toast.success('Task updated');
      } else {
        await axios.post(`${API}/wfh-tracker/tasks`, payload);
        toast.success('Task added');
      }
      setShowTaskDialog(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const filteredTasks = tasks.filter(t => {
    if (filterEmployee !== 'all' && t.employee_name !== filterEmployee) return false;
    if (filterStatus !== 'all' && (t.status || '').toLowerCase() !== filterStatus) return false;
    if (dateFrom && (t.date || '') < dateFrom) return false;
    if (dateTo && (t.date || '') > dateTo) return false;
    if (search) {
      const q = search.toLowerCase();
      return (t.task || '').toLowerCase().includes(q) ||
        (t.milestone || '').toLowerCase().includes(q) ||
        (t.employee_name || '').toLowerCase().includes(q) ||
        (t.comments || '').toLowerCase().includes(q);
    }
    return true;
  });

  const summary = {
    total: tasks.length,
    todo: tasks.filter(t => {
      const v = (t.status || '').toLowerCase();
      return !v || v === 'not started' || v === 'todo' || v === 'to do';
    }).length,
    in_progress: tasks.filter(t => (t.status || '').toLowerCase() === 'in progress').length,
    awaiting: tasks.filter(t => (t.status || '').toLowerCase() === 'awaiting feedback').length,
    completed: tasks.filter(t => {
      const v = (t.status || '').toLowerCase();
      return v === 'completed' || v === 'done';
    }).length,
    overdue: tasks.filter(t => {
      const today = new Date().toISOString().slice(0, 10);
      const v = (t.status || '').toLowerCase();
      return t.due_date && t.due_date < today && v !== 'completed' && v !== 'done';
    }).length,
    employees: employees.length,
  };

  return (
    <div className="space-y-6" data-testid="wfh-tracker-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">WFH Task Tracker</h1>
          <p className="text-muted-foreground mt-1">Import the team's WFH spreadsheet and download per-employee or full reports</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={loadData} data-testid="refresh-btn"><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
          <Button variant="outline" onClick={openAddEmployee} data-testid="add-employee-btn"><Users className="h-4 w-4 mr-2" /> Add Employee</Button>
          <Button variant="outline" onClick={() => openAddTask('')} data-testid="add-task-btn"><FileSpreadsheet className="h-4 w-4 mr-2" /> Add Task</Button>
          <Button variant="outline" onClick={exportAll} disabled={tasks.length === 0} data-testid="export-all-btn">
            <FileDown className="h-4 w-4 mr-2" /> Export All Employees
          </Button>
          <Button onClick={() => setShowImport(true)} data-testid="import-btn">
            <Upload className="h-4 w-4 mr-2" /> Import Spreadsheet
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center"><FileSpreadsheet className="h-5 w-5 text-blue-600" /></div><div><p className="text-xs text-muted-foreground">Total Tasks</p><p className="text-xl font-bold font-mono">{summary.total}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center"><Users className="h-5 w-5 text-emerald-600" /></div><div><p className="text-xs text-muted-foreground">Employees</p><p className="text-xl font-bold font-mono">{summary.employees}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center"><Clock className="h-5 w-5 text-slate-600" /></div><div><p className="text-xs text-muted-foreground">To Do</p><p className="text-xl font-bold font-mono">{summary.todo}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center"><Clock className="h-5 w-5 text-amber-600" /></div><div><p className="text-xs text-muted-foreground">In Progress</p><p className="text-xl font-bold font-mono">{summary.in_progress}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center"><MessageSquare className="h-5 w-5 text-purple-600" /></div><div><p className="text-xs text-muted-foreground">Awaiting Feedback</p><p className="text-xl font-bold font-mono">{summary.awaiting}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center"><CheckCircle2 className="h-5 w-5 text-green-600" /></div><div><p className="text-xs text-muted-foreground">Done</p><p className="text-xl font-bold font-mono">{summary.completed}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center"><AlertTriangle className="h-5 w-5 text-red-600" /></div><div><p className="text-xs text-muted-foreground">Overdue</p><p className="text-xl font-bold font-mono">{summary.overdue}</p></div></CardContent></Card>
      </div>

      {/* Filters */}
      <Card><CardContent className="p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="relative md:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search task, milestone, employee, comments..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} data-testid="search-input" />
        </div>
        <Select value={filterEmployee} onValueChange={setFilterEmployee}>
          <SelectTrigger data-testid="filter-employee"><SelectValue placeholder="Employee" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Employees</SelectItem>
            {employees.map(e => <SelectItem key={e.employee_name} value={e.employee_name}>{e.employee_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="not started">Not Started</SelectItem>
            <SelectItem value="in progress">In Progress</SelectItem>
            <SelectItem value="awaiting feedback">Awaiting Feedback</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="From" data-testid="date-from" />
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="To" data-testid="date-to" />
        </div>
      </CardContent></Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="board" data-testid="board-tab">Board</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tasks-tab">Tasks ({filteredTasks.length})</TabsTrigger>
          <TabsTrigger value="employees" data-testid="employees-tab">Employees ({employees.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="board" className="mt-4">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground animate-pulse">Loading...</div>
          ) : filteredTasks.length === 0 ? (
            <Card><CardContent className="p-12 text-center text-muted-foreground">
              <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>{tasks.length === 0 ? 'No data yet — import the WFH spreadsheet or add a task.' : 'No tasks match the current filters.'}</p>
            </CardContent></Card>
          ) : (
            <BoardView tasks={filteredTasks} onEditTask={openEditTask} />
          )}
        </TabsContent>

        <TabsContent value="tasks" className="mt-4">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground animate-pulse">Loading...</div>
          ) : filteredTasks.length === 0 ? (
            <Card><CardContent className="p-12 text-center text-muted-foreground">
              <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>{tasks.length === 0 ? 'No data yet — import the WFH spreadsheet to get started.' : 'No tasks match the current filters.'}</p>
              {tasks.length === 0 && <Button className="mt-3" onClick={() => setShowImport(true)}><Upload className="h-4 w-4 mr-2" /> Import Spreadsheet</Button>}
            </CardContent></Card>
          ) : (
            <Card>
              <ScrollArea className="max-h-[640px]">
                <table className="w-full text-sm" data-testid="tasks-table">
                  <thead className="sticky top-0 bg-background border-b z-10">
                    <tr className="text-left">
                      <th className="p-2">Employee</th>
                      <th className="p-2">Date</th>
                      <th className="p-2">Task / Activity</th>
                      <th className="p-2">Priority</th>
                      <th className="p-2">Milestone</th>
                      <th className="p-2">Due</th>
                      <th className="p-2">Status</th>
                      <th className="p-2 text-right">Progress</th>
                      <th className="p-2">Manager</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTasks.map(t => (
                      <tr key={t.id} className="border-b hover:bg-muted/40" data-testid={`task-row-${t.id}`}>
                        <td className="p-2">
                          <p className="font-medium">{t.employee_name}</p>
                          <p className="text-[11px] text-muted-foreground">{t.department || '-'} • {t.position || '-'}</p>
                        </td>
                        <td className="p-2 text-xs">{t.date || '-'}<br /><span className="text-muted-foreground">{t.start_time || ''}{t.end_time ? ` – ${t.end_time}` : ''}</span></td>
                        <td className="p-2 max-w-[280px]">
                          <p className="font-medium line-clamp-2">{t.task}</p>
                          {t.comments && <p className="text-[11px] text-muted-foreground line-clamp-1">{t.comments}</p>}
                        </td>
                        <td className="p-2">{t.priority && <Badge className={PRIORITY_BADGE(t.priority)}>{t.priority}</Badge>}</td>
                        <td className="p-2 text-xs">{t.milestone || '-'}</td>
                        <td className="p-2 text-xs">{t.due_date || '-'}</td>
                        <td className="p-2">{t.status && <Badge className={STATUS_BADGE(t.status)}>{t.status}</Badge>}</td>
                        <td className="p-2 text-right tabular-nums">{t.progress != null ? `${t.progress}%` : '-'}</td>
                        <td className="p-2 text-xs">{t.manager || '-'}</td>
                        <td className="p-2">
                          <div className="flex gap-1 justify-end">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEditTask(t)} data-testid={`edit-task-${t.id}`} title="Edit">
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            {isAdmin && (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteTask(t.id)} data-testid={`delete-task-${t.id}`} title="Delete">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="employees" className="mt-4">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground animate-pulse">Loading...</div>
          ) : employees.length === 0 ? (
            <Card><CardContent className="p-12 text-center text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No employees yet. Import the spreadsheet first.</p>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {employees.map(emp => (
                <Card key={emp.employee_name} data-testid={`employee-card-${emp.employee_name}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <CardTitle className="text-base truncate">{emp.employee_name}</CardTitle>
                        <CardDescription className="truncate">{emp.position || '-'} • {emp.department || '-'}</CardDescription>
                        {emp.manager && <p className="text-[11px] text-muted-foreground mt-0.5">Manager: {emp.manager}</p>}
                      </div>
                      <div className="flex gap-0.5 shrink-0">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEditEmployee(emp)} data-testid={`edit-employee-${emp.employee_name}`} title="Edit"><Edit className="h-3.5 w-3.5" /></Button>
                        {isAdmin && emp.id && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteEmployee(emp)} data-testid={`delete-employee-${emp.employee_name}`} title="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="p-2 bg-muted rounded">
                        <p className="text-lg font-bold">{emp.total_tasks}</p>
                        <p className="text-[10px] text-muted-foreground">Total</p>
                      </div>
                      <div className="p-2 bg-green-50 rounded">
                        <p className="text-lg font-bold text-green-700">{emp.completed}</p>
                        <p className="text-[10px] text-muted-foreground">Done</p>
                      </div>
                      <div className="p-2 bg-amber-50 rounded">
                        <p className="text-lg font-bold text-amber-700">{emp.in_progress}</p>
                        <p className="text-[10px] text-muted-foreground">In Prog.</p>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-muted-foreground">Avg Progress</span>
                        <span className="font-semibold">{emp.avg_progress}%</span>
                      </div>
                      <div className="h-1.5 bg-muted rounded overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: `${Math.min(emp.avg_progress, 100)}%` }} />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => openAddTask(emp.employee_name)} data-testid={`add-task-for-${emp.employee_name}`}>
                        <Plus className="h-3.5 w-3.5 mr-1" /> Add Task
                      </Button>
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => exportEmployee(emp.employee_name)} data-testid={`export-employee-${emp.employee_name}`} disabled={emp.total_tasks === 0}>
                        <Download className="h-3.5 w-3.5 mr-1" /> Report
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Employee Dialog (Add/Edit) */}
      <Dialog open={showEmpDialog} onOpenChange={setShowEmpDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{empForm.id ? 'Edit Employee' : 'Add Employee'}</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-3">
            <div className="space-y-1"><Label>Full Name *</Label><Input value={empForm.name} onChange={e => setEmpForm({ ...empForm, name: e.target.value })} placeholder="e.g. Jane Doe" data-testid="emp-name-input" autoFocus /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Department</Label><Input value={empForm.department} onChange={e => setEmpForm({ ...empForm, department: e.target.value })} placeholder="Marketing" data-testid="emp-dept-input" /></div>
              <div className="space-y-1"><Label>Position</Label><Input value={empForm.position} onChange={e => setEmpForm({ ...empForm, position: e.target.value })} placeholder="Marketing Officer" data-testid="emp-position-input" /></div>
            </div>
            <div className="space-y-1"><Label>Manager</Label><Input value={empForm.manager} onChange={e => setEmpForm({ ...empForm, manager: e.target.value })} placeholder="Manager name" data-testid="emp-manager-input" /></div>
            {empForm.id && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Renaming will also update all of this employee's existing tasks.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmpDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveEmployee} data-testid="emp-submit">{empForm.id ? 'Save Changes' : 'Add Employee'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Dialog (Add/Edit) */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{taskForm.id ? 'Edit Task' : 'Add Task'}</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-3 max-h-[70vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Employee *</Label>
                <Select value={taskForm.employee_name} onValueChange={(v) => {
                  const emp = employees.find(e => e.employee_name === v);
                  setTaskForm({ ...taskForm, employee_name: v,
                    department: emp?.department || taskForm.department,
                    position: emp?.position || taskForm.position,
                    manager: emp?.manager || taskForm.manager });
                }}>
                  <SelectTrigger data-testid="task-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>
                    {employees.map(e => <SelectItem key={e.employee_name} value={e.employee_name}>{e.employee_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Date</Label><Input type="date" value={taskForm.date} onChange={e => setTaskForm({ ...taskForm, date: e.target.value })} data-testid="task-date-input" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>Department</Label><Input value={taskForm.department} onChange={e => setTaskForm({ ...taskForm, department: e.target.value })} /></div>
              <div className="space-y-1"><Label>Position</Label><Input value={taskForm.position} onChange={e => setTaskForm({ ...taskForm, position: e.target.value })} /></div>
              <div className="space-y-1"><Label>Manager</Label><Input value={taskForm.manager} onChange={e => setTaskForm({ ...taskForm, manager: e.target.value })} /></div>
            </div>
            <div className="space-y-1"><Label>Task / Activity *</Label><Textarea rows={2} value={taskForm.task} onChange={e => setTaskForm({ ...taskForm, task: e.target.value })} placeholder="What needs to be done?" data-testid="task-task-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Start Time</Label><Input type="time" value={taskForm.start_time} onChange={e => setTaskForm({ ...taskForm, start_time: e.target.value })} /></div>
              <div className="space-y-1"><Label>End Time</Label><Input type="time" value={taskForm.end_time} onChange={e => setTaskForm({ ...taskForm, end_time: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>Priority</Label>
                <Select value={taskForm.priority || ''} onValueChange={v => setTaskForm({ ...taskForm, priority: v })}>
                  <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Status</Label>
                <Select value={taskForm.status || ''} onValueChange={v => setTaskForm({ ...taskForm, status: v })}>
                  <SelectTrigger data-testid="task-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Not Started">Not Started</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Awaiting Feedback">Awaiting Feedback</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Progress %</Label><Input type="number" min="0" max="100" value={taskForm.progress} onChange={e => setTaskForm({ ...taskForm, progress: e.target.value })} placeholder="0-100" data-testid="task-progress-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Milestone / Deliverable</Label><Input value={taskForm.milestone} onChange={e => setTaskForm({ ...taskForm, milestone: e.target.value })} /></div>
              <div className="space-y-1"><Label>Due Date</Label><Input type="date" value={taskForm.due_date} onChange={e => setTaskForm({ ...taskForm, due_date: e.target.value })} /></div>
            </div>
            <div className="space-y-1"><Label>Challenges / Blockers</Label><Textarea rows={2} value={taskForm.blockers} onChange={e => setTaskForm({ ...taskForm, blockers: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Support Needed</Label><Input value={taskForm.support} onChange={e => setTaskForm({ ...taskForm, support: e.target.value })} /></div>
              <div className="space-y-1"><Label>Approved by Manager</Label><Input value={taskForm.approved_by} onChange={e => setTaskForm({ ...taskForm, approved_by: e.target.value })} /></div>
            </div>
            <div className="space-y-1"><Label>Comments / Notes</Label><Textarea rows={2} value={taskForm.comments} onChange={e => setTaskForm({ ...taskForm, comments: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Next Action</Label><Input value={taskForm.next_action} onChange={e => setTaskForm({ ...taskForm, next_action: e.target.value })} /></div>
              <div className="space-y-1"><Label>Follow-up Date</Label><Input type="date" value={taskForm.follow_up_date} onChange={e => setTaskForm({ ...taskForm, follow_up_date: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveTask} data-testid="task-submit">{taskForm.id ? 'Save Changes' : 'Add Task'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent>
          <DialogHeader><DialogTitle>Import WFH Spreadsheet</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-3">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors" onClick={() => fileInputRef.current?.click()}>
              <FileSpreadsheet className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm font-medium">{importFile ? importFile.name : 'Click to select .xlsx file'}</p>
              <p className="text-xs text-muted-foreground mt-1">Headers expected: Employee Name, Department, Manager, Date, Task / Activity, Status, Progress %, etc.</p>
              <input ref={fileInputRef} type="file" className="hidden" accept=".xlsx,.xlsm" onChange={e => setImportFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={replaceAll} onChange={e => setReplaceAll(e.target.checked)} data-testid="replace-all-checkbox" />
              <span><strong>Replace all existing tasks</strong> (clear database before importing)</span>
            </label>
            <div className="text-xs text-muted-foreground bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2">
              <Calendar className="h-3.5 w-3.5 mt-0.5 text-amber-700" />
              <span>Rows missing an Employee Name or Task / Activity will be skipped automatically. Default sample row "Example Employee" is also skipped.</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImport(false)}>Cancel</Button>
            <Button onClick={handleImport} disabled={!importFile || importing} data-testid="import-submit">
              {importing ? 'Importing...' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WFHTrackerPage;

// ---- Board (Kanban-style) view ----
const BoardView = ({ tasks, onEditTask }) => {
  const COLUMNS = [
    { key: 'todo', label: 'To Do', accent: 'bg-slate-100 border-slate-300', dot: 'bg-slate-500', match: (s) => !s || ['not started', 'todo', 'to do'].includes(s.toLowerCase()) },
    { key: 'in_progress', label: 'In Progress', accent: 'bg-amber-50 border-amber-300', dot: 'bg-amber-500', match: (s) => (s || '').toLowerCase() === 'in progress' },
    { key: 'awaiting', label: 'Awaiting Feedback', accent: 'bg-purple-50 border-purple-300', dot: 'bg-purple-500', match: (s) => (s || '').toLowerCase() === 'awaiting feedback' },
    { key: 'done', label: 'Done', accent: 'bg-green-50 border-green-300', dot: 'bg-green-500', match: (s) => ['completed', 'done'].includes((s || '').toLowerCase()) },
  ];

  const today = new Date().toISOString().slice(0, 10);
  const buckets = COLUMNS.map(c => ({ ...c, items: tasks.filter(t => c.match(t.status)) }));

  const priorityClass = (p) => {
    const v = (p || '').toLowerCase();
    if (v.includes('high')) return 'bg-red-100 text-red-700';
    if (v.includes('med')) return 'bg-blue-100 text-blue-700';
    if (v.includes('low')) return 'bg-slate-100 text-slate-600';
    return '';
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="board-view">
      {buckets.map(col => (
        <div key={col.key} className={`rounded-lg border-2 ${col.accent} p-3 flex flex-col`} data-testid={`board-col-${col.key}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full ${col.dot}`}></span>
              <h3 className="font-semibold text-sm">{col.label}</h3>
            </div>
            <Badge variant="outline" className="bg-white text-xs">{col.items.length}</Badge>
          </div>
          <ScrollArea className="max-h-[560px]">
            <div className="space-y-2 pr-1">
              {col.items.length === 0 && (
                <p className="text-xs text-muted-foreground italic text-center py-6">No tasks</p>
              )}
              {col.items.map(t => {
                const overdue = t.due_date && t.due_date < today && col.key !== 'done';
                return (
                  <Card key={t.id} className="cursor-pointer hover:shadow-md transition-shadow border" onClick={() => onEditTask(t)} data-testid={`board-card-${t.id}`}>
                    <CardContent className="p-3 space-y-2">
                      <p className="text-sm font-medium line-clamp-2">{t.task}</p>
                      <div className="flex items-center justify-between gap-1 flex-wrap">
                        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground min-w-0">
                          <Users className="h-3 w-3 shrink-0" />
                          <span className="truncate">{t.employee_name}</span>
                        </div>
                        {t.priority && <Badge className={`text-[10px] ${priorityClass(t.priority)}`}>{t.priority}</Badge>}
                      </div>
                      {t.milestone && <p className="text-[11px] text-muted-foreground truncate">📌 {t.milestone}</p>}
                      <div className="flex items-center justify-between text-[11px]">
                        <span className={`flex items-center gap-1 ${overdue ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}>
                          <Calendar className="h-3 w-3" />
                          {t.due_date || t.date || '-'}
                          {overdue && ' (overdue)'}
                        </span>
                        {t.progress != null && <span className="font-mono">{t.progress}%</span>}
                      </div>
                      {t.progress != null && (
                        <div className="h-1 bg-white/70 rounded overflow-hidden">
                          <div className={`h-full ${col.key === 'done' ? 'bg-green-500' : col.key === 'awaiting' ? 'bg-purple-500' : 'bg-amber-500'}`} style={{ width: `${Math.min(t.progress, 100)}%` }} />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      ))}
    </div>
  );
};
